import React, { useState } from 'react';
import {
  IconButton,
  InputAdornment,
  TextField,
  TextFieldProps,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { styled } from '@mui/system';

const CustomTextField = styled(TextField)(({ theme }: any) => ({
  '& .MuiInputBase-root': {
    backgroundColor: 'transparent',
    color: theme.palette.text.primary,
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: theme.palette.primary.light,
      borderWidth: 2,
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
      borderWidth: 2,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
      borderWidth: 2,
    },
  },
  '& .MuiInputLabel-root': {
    color: theme.palette.text.secondary,
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: theme.palette.primary.main,
  },
  '& input:-webkit-autofill': {
    boxShadow: '0 0 0 1000px transparent inset',
    WebkitBoxShadow: '0 0 0 1000px transparent inset',
    WebkitTextFillColor: theme.palette.text.primary,
    transition: 'background-color 9999s ease-in-out 0s',
  },
}));

export type TextInputProps = TextFieldProps & {
  showPasswordToggle?: boolean;
  showPasswordLabel?: string;
  hidePasswordLabel?: string;
};

const TextInput: React.FC<TextInputProps> = ({
  type,
  showPasswordToggle = false,
  showPasswordLabel = 'Show password',
  hidePasswordLabel = 'Hide password',
  slotProps,
  InputProps,
  ...props
}) => {
  const [passwordVisible, setPasswordVisible] = useState(false);
  const isPasswordField = type === 'password';
  const usePasswordToggle = showPasswordToggle && isPasswordField;

  if (!usePasswordToggle) {
    return (
      <CustomTextField
        {...props}
        type={type}
        InputProps={InputProps}
        slotProps={slotProps}
      />
    );
  }

  const slotInput =
    slotProps?.input && typeof slotProps.input === 'object' ? slotProps.input : {};

  return (
    <CustomTextField
      {...props}
      type={passwordVisible ? 'text' : type}
      slotProps={{
        ...slotProps,
        input: {
          ...InputProps,
          ...slotInput,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                type="button"
                aria-label={passwordVisible ? hidePasswordLabel : showPasswordLabel}
                onClick={() => setPasswordVisible((visible) => !visible)}
                onMouseDown={(e) => e.preventDefault()}
                edge="end"
                size="small"
                sx={{ color: 'primary.main' }}
              >
                {passwordVisible ? (
                  <VisibilityOff fontSize="small" />
                ) : (
                  <Visibility fontSize="small" />
                )}
              </IconButton>
            </InputAdornment>
          ),
        },
      }}
    />
  );
};

export default TextInput;
